import { Seat, SEATS } from "@ravalmatic/bridge-engine/core";
import { GameVars } from "../../../GameVars";
import { PersistentDataManager } from "../../../PersistentDataManager";
import { DevelopmentSeats } from "../development/DevelopmentContract";
import { BoardPhase, ContractOutcome } from "../../../Types";

export class DevelopmentMenu extends Phaser.GameObjects.Container {

    private static readonly GROUP_WIDTH = 188;
    private readonly dealerGroup: Phaser.GameObjects.Container;
    private readonly declarerGroup: Phaser.GameObjects.Container;
    private readonly endBiddingGroup: Phaser.GameObjects.Container;
    private readonly endBiddingButton: Phaser.GameObjects.Rectangle;
    private readonly resultGroup: Phaser.GameObjects.Container;
    private readonly resultButtons: Phaser.GameObjects.Rectangle[];
    private readonly seatControls: {
        role: keyof DevelopmentSeats;
        seat: Seat;
        button: Phaser.GameObjects.Rectangle;
        label: Phaser.GameObjects.Text;
    }[] = [];

    constructor(
        scene: Phaser.Scene,
        private selection: DevelopmentSeats,
        private readonly onEndBidding: (selection: DevelopmentSeats) => void,
        private readonly onForceContractResult: (outcome: ContractOutcome) => void
    ) {

        super(scene);

        this.selection = { ...selection };
        this.setDepth(1000);
        this.dealerGroup = this.createSeatGroup("DEALER", "dealer");
        this.declarerGroup = this.createSeatGroup("DECLARER", "declarer");
        this.endBiddingGroup = new Phaser.GameObjects.Container(scene);
        this.endBiddingButton = this.createActionButton(this.endBiddingGroup, "END BIDDING", 0, () => {
            this.onEndBidding({ ...this.selection });
        });
        this.resultGroup = new Phaser.GameObjects.Container(scene);
        this.resultButtons = [
            this.createActionButton(this.resultGroup, "CONTRACT MADE", 0, () => this.onForceContractResult("made")),
            this.createActionButton(this.resultGroup, "CONTRACT DOWN", 56, () => this.onForceContractResult("defeated"))
        ];
        this.add([this.dealerGroup, this.declarerGroup, this.endBiddingGroup, this.resultGroup]);
        this.refreshSelection();
        this.setPhase(BoardPhase.BIDDING);
        this.relayout();
    }

    public setPhase(phase: BoardPhase): void {

        const bidding = phase === BoardPhase.BIDDING || phase === BoardPhase.CONTRACT;
        const playing = phase === BoardPhase.PLAY;
        this.setVisible(bidding || playing);
        [this.dealerGroup, this.declarerGroup, this.endBiddingGroup].forEach(group => group.setVisible(bidding));
        [...this.seatControls.map(control => control.button), this.endBiddingButton].forEach(button => {
            if (bidding) button.setInteractive();
            else button.disableInteractive();
        });
        this.resultGroup.setVisible(playing);
        this.resultButtons.forEach(button => {
            if (playing) button.setInteractive();
            else button.disableInteractive();
        });
    }

    public relayout(): void {

        const leftX = GameVars.gameWidth / 4 - DevelopmentMenu.GROUP_WIDTH / 2 - 30;
        const rightX = GameVars.gameWidth * .75 - DevelopmentMenu.GROUP_WIDTH / 2 + 30;
        const y = GameVars.gameHeight * 2 / 3;
        this.dealerGroup.setPosition(leftX, y);
        [this.dealerGroup, this.declarerGroup, this.endBiddingGroup, this.resultGroup]
            .forEach(group => group.setScale(1, GameVars.scaleY));
        this.declarerGroup.setPosition(
            rightX, y
        );
        this.endBiddingGroup.setPosition(this.declarerGroup.x, this.declarerGroup.y - 56 * GameVars.scaleY);
        this.resultGroup.setPosition(leftX, y);
    }

    private createActionButton(
        group: Phaser.GameObjects.Container, title: string, y: number, onClick: () => void
    ): Phaser.GameObjects.Rectangle {

        const button = new Phaser.GameObjects.Rectangle(
            this.scene, 0, y, DevelopmentMenu.GROUP_WIDTH, 40, 0x163f46
        ).setOrigin(0).setStrokeStyle(2, 0x6eb8be).setInteractive({ useHandCursor: true });
        button.on(Phaser.Input.Events.POINTER_UP, () => {
            if (this.visible && group.visible && button.input?.enabled) {
                onClick();
            }
        });
        const label = new Phaser.GameObjects.Text(
            this.scene, DevelopmentMenu.GROUP_WIDTH / 2, y + 20, title, {
                fontFamily: "Baloo2-SemiBold", fontSize: "20px", color: "#ffffff"
            }
        ).setOrigin(.5);
        group.add([button, label]);
        return button;
    }

    private refreshSelection(): void {

        this.seatControls.forEach(({ role, seat, button, label }) => {
            const selected = this.selection[role] === seat;
            button.setFillStyle(selected ? 0x147b98 : 0x163f46)
                .setStrokeStyle(2, selected ? 0xffda72 : 0x6eb8be);
            label.setColor(selected ? "#ffda72" : "#ffffff");
        });
    }

    private createSeatGroup(title: string, role: keyof DevelopmentSeats): Phaser.GameObjects.Container {

        const group = new Phaser.GameObjects.Container(this.scene);
        group.add(new Phaser.GameObjects.Text(this.scene, 0, 0, title, {
            fontFamily: "Baloo2-SemiBold",
            fontSize: "18px",
            color: "#c8eaf5",
        }));
        SEATS.forEach((seat: Seat) => {
            const selected = this.selection[role] === seat;
            const button = new Phaser.GameObjects.Rectangle(
                this.scene, seat * 48, 28, 44, 40, selected ? 0x147b98 : 0x163f46
            ).setOrigin(0).setStrokeStyle(2, selected ? 0xffda72 : 0x6eb8be)
                .setInteractive({ useHandCursor: true });
            const label = new Phaser.GameObjects.Text(this.scene, seat * 48 + 22, 48,
                ["N", "E", "S", "W"][seat], {
                    fontFamily: "Baloo2-SemiBold",
                    fontSize: "24px",
                    color: selected ? "#ffda72" : "#ffffff",
                }).setOrigin(.5);
            button.on(Phaser.Input.Events.POINTER_UP, () => {
                if (!this.visible || !group.visible || !button.input?.enabled) {
                    return;
                }
                this.selection = { ...this.selection, [role]: seat };
                PersistentDataManager.updateGameData("developmentPreferences", { ...this.selection });
                this.refreshSelection();
            });
            this.seatControls.push({ role, seat, button, label });
            group.add([button, label]);
        });
        return group;
    }
}
